import {
  Home,
  LayoutGrid,
  Leaf,
  MapPin,
  Maximize2,
  Sparkles,
  TrendingUp
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ApartmentIndex } from '@services/livability/types'

import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'

interface ApartmentIndexDisplayProps {
  index: ApartmentIndex | null | undefined
  compact?: boolean
}

const getScoreColor = (score: number): string => {
  const clampedScore = Math.max(0, Math.min(100, score))
  let r: number, g: number, b: number

  if (clampedScore < 50) {
    const t = clampedScore / 50
    r = Math.round(220 + (234 - 220) * t)
    g = Math.round(38 + (179 - 38) * t)
    b = Math.round(38 + (8 - 38) * t)
  } else {
    const t = (clampedScore - 50) / 50
    r = Math.round(234 + (34 - 234) * t)
    g = Math.round(179 + (197 - 179) * t)
    b = Math.round(8 + (94 - 8) * t)
  }

  return `rgb(${r}, ${g}, ${b})`
}

const getScoreBadgeVariant = (
  score: number
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (score >= 80) return 'default'
  if (score >= 60) return 'secondary'
  if (score >= 40) return 'outline'
  return 'destructive'
}

const getScoreLabelKey = (score: number): string => {
  if (score >= 80) return 'index.ratings.excellent'
  if (score >= 60) return 'index.ratings.good'
  if (score >= 40) return 'index.ratings.average'
  return 'index.ratings.belowAverage'
}

export function ApartmentIndexDisplay({
  index,
  compact = false
}: ApartmentIndexDisplayProps) {
  const { t } = useTranslation('livability')

  if (!index) {
    return null
  }

  const components = index?.components
    ? [
        {
          label: t('index.labels.price'),
          value: index.components.price_score,
          weight: index.weights.price,
          icon: TrendingUp
        },
        {
          label: t('index.labels.amenities'),
          value: index.components.amenities_score,
          weight: index.weights.amenities,
          icon: MapPin
        },
        {
          label: t('index.labels.size'),
          value: index.components.size_score,
          weight: index.weights.size,
          icon: Maximize2
        },
        {
          label: t('index.labels.room'),
          value: index.components.room_efficiency_score,
          weight: index.weights.room,
          icon: LayoutGrid
        },
        {
          label: t('index.labels.features'),
          value: index.components.feature_score,
          weight: index.weights.features,
          icon: Sparkles
        },
        {
          label: t('index.labels.environment'),
          value: index.components.environment_score,
          weight: index.weights.environment,
          icon: Leaf
        }
      ]
    : []

  if (compact) {
    return (
      <div className="space-y-3 rounded-2xl bg-ink/[0.03] p-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-primary" />
            <div>
              <div className="text-xs text-muted-foreground">
                {t('index.qualityRating')}
              </div>
              <div
                className="font-display text-2xl font-bold"
                style={{ color: getScoreColor(index.index) }}
              >
                {index.index}/100
              </div>
            </div>
          </div>
          <Badge variant={getScoreBadgeVariant(index.index)} className="ml-auto">
            {t(getScoreLabelKey(index.index))}
          </Badge>
        </div>

        <div className="border-t border-primary/20" />

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <div>
              <div className="text-xs text-muted-foreground">
                {t('index.labels.amenities')}
              </div>
              <div
                className="text-lg font-bold"
                style={{ color: getScoreColor(index.components.amenities_score) }}
              >
                {index.components.amenities_score}/100
              </div>
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-muted-foreground">
              {t('index.weight')}
            </div>
            <div className="text-sm font-semibold text-primary">
              {index.weights.amenities}%
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              {t('index.title')}
            </CardTitle>
            <CardDescription>
              {t(`detail.categories.${index.category}`)} •{' '}
              {t('index.comparedApartments')}:{' '}
              {index.metrics.comparable_apartments}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end">
            <span
              className="font-display text-4xl font-bold leading-none"
              style={{ color: getScoreColor(index.index) }}
            >
              {index.index}
            </span>
            <Badge variant={getScoreBadgeVariant(index.index)} className="mt-2">
              {t(getScoreLabelKey(index.index))}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-2xl bg-ink/[0.03] p-3">
          <div className="text-sm text-muted-foreground">
            {t('index.pricePerM2')}
          </div>
          <div className="font-display text-xl font-semibold">
            {index.metrics.price_per_m2 != null
              ? `${index.metrics.price_per_m2} €/m²`
              : 'N/A'}
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-semibold">{t('index.components')}</h4>
          {components.map((component) => {
            const Icon = component.icon
            return (
              <div key={component.label} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{component.label}</span>
                    <span className="text-xs text-muted-foreground">
                      ({component.weight}%)
                    </span>
                  </div>
                  <span
                    className="font-semibold"
                    style={{ color: getScoreColor(component.value) }}
                  >
                    {component.value}
                  </span>
                </div>
                <Progress value={component.value} className="h-2" />
              </div>
            )
          })}
        </div>

        <div className="border-t border-ink/[0.06] pt-3 text-xs text-muted-foreground">
          {t('index.footer', {
            count: index.metrics.comparable_apartments
          })}
        </div>
      </CardContent>
    </Card>
  )
}
